import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import { useTranslation } from "next-i18next";
import { forwardRef, useImperativeHandle, useState } from "react";

export type ConfirmationWithMessageDialogShowArgs = {
	text: string;
	onYes: (message: string) => void;
	onNo?: () => void;
	className?: string;
	messageLabel?: string;
	messageRequired?: boolean;
};

export type ConfirmationWithMessageDialogHandle = {
	show: (args: ConfirmationWithMessageDialogShowArgs) => void;
	close: () => void;
};

const ConfirmationWithMessageDialog = forwardRef<ConfirmationWithMessageDialogHandle>(
	(_props, ref) => {
		const { t } = useTranslation();

		const [open, setOpen] = useState(false);
		const [options, setOptions] = useState<ConfirmationWithMessageDialogShowArgs | null>(null);
		const [message, setMessage] = useState("");
		const [error, setError] = useState<string | null>(null);

		// expose imperative API
		useImperativeHandle(
			ref,
			() => ({
				show: (args: ConfirmationWithMessageDialogShowArgs) => {
					setOptions(args);
					setMessage("");
					setError(null);
					setOpen(true);
				},
				close: () => {
					handleCloseInternal(false);
				},
			}),
			[],
		);

		const handleCloseInternal = (invokeNoCallback: boolean) => {
			if (invokeNoCallback && options?.onNo) {
				options.onNo();
			}
			setOpen(false);
			setOptions(null);
			setMessage("");
			setError(null);
		};

		const handleYesClick = () => {
			// Validate message if required
			if (options?.messageRequired && !message.trim()) {
				setError(t("validation.reason.required"));
				return;
			}

			if (options?.onYes) {
				options.onYes(message);
			}
			setOpen(false);
			setOptions(null);
			setMessage("");
			setError(null);
		};

		const handleNoClick = () => {
			handleCloseInternal(true);
		};

		const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			setMessage(event.target.value);
			if (error) {
				setError(null);
			}
		};

		return (
			<Dialog
				open={open}
				data-testid="confirmation-with-message-dialog"
				aria-describedby="alert-dialog-description"
				className={options?.className}>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">{options?.text}</DialogContentText>
					<TextField
						onChange={handleMessageChange}
						className="w-100"
						sx={{ marginTop: 2 }}
						error={!!error}
						helperText={error || ""}
						value={message}
						label={options?.messageLabel || t("reason")}
						variant="outlined"
						rows={4}
						multiline
						autoFocus
					/>
				</DialogContent>
				<DialogActions>
					<Button data-testid="yes-button" onClick={handleYesClick}>
						{t("yes")}
					</Button>
					<Button data-testid="no-button" onClick={handleNoClick}>
						{t("no")}
					</Button>
				</DialogActions>
			</Dialog>
		);
	},
);

ConfirmationWithMessageDialog.displayName = "ConfirmationWithMessageDialog";

export default ConfirmationWithMessageDialog;
